// M7 – Danh sách phiếu hoàn (/pos/refunds): duyệt / từ chối / chi tiền
import { useMemo, useState } from 'react';
import { Badge, Input, Modal, Select, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, HandCoins, X } from 'lucide-react';
import { METHOD_LABEL, METHODS_NEED_REF, branchName, type Refund, type RefundStatus } from './data';
import usePosStore from './store';
import { REFUND_STATUS, StatusPill, branchOptions, fmtDateTime, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';

type TabKey = RefundStatus | 'all';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'pending', label: 'Chờ duyệt' }, { key: 'approved', label: 'Đã duyệt chờ chi' }, { key: 'paid', label: 'Đã chi' },
  { key: 'rejected', label: 'Từ chối' }, { key: 'all', label: 'Tất cả' },
];

export default function PosRefunds() {
  const refunds = usePosStore(s => s.refunds);
  const orders = usePosStore(s => s.orders);
  const workingBranch = usePosStore(s => s.workingBranch);
  const approveRefund = usePosStore(s => s.approveRefund);
  const rejectRefund = usePosStore(s => s.rejectRefund);
  const payRefund = usePosStore(s => s.payRefund);
  const { can, role, userName } = usePosAuth();
  const [params] = useSearchParams();
  const highlight = params.get('id');
  const [modal, ctx] = Modal.useModal();

  const initialTab = (highlight && refunds.find(r => r.id === highlight)?.status) || 'pending';
  const [tab, setTab] = useState<TabKey>(initialTab as TabKey);
  const [branch, setBranch] = useState<string | undefined>(role === 'cashier' ? workingBranch : undefined);
  const [q, setQ] = useState('');
  const [reject, setReject] = useState<{ id: string; reason: string } | null>(null);
  const [paying, setPaying] = useState<{ r: Refund; ref: string } | null>(null);

  const customerOf = useMemo(() => new Map(orders.map(o => [o.id, o.customerName])), [orders]);
  const base = useMemo(() => refunds.filter(r =>
    (!branch || r.branchId === branch) &&
    (!q || r.id.toLowerCase().includes(q.toLowerCase()) || r.orderId.toLowerCase().includes(q.toLowerCase()) || (customerOf.get(r.orderId) || '').toLowerCase().includes(q.toLowerCase()))),
  [refunds, branch, q, customerOf]);
  const count = (k: TabKey) => base.filter(r => k === 'all' || r.status === k).length;
  const data = base.filter(r => tab === 'all' || r.status === tab);

  // Duyệt: có pos.approve và không phải người tạo (admin được duyệt phiếu của mình)
  const canApprove = (r: Refund) => can('approve') && (r.createdBy !== userName || role === 'admin');
  const canPay = (r: Refund) => can('create') && r.branchId === workingBranch;

  const columns: ColumnsType<Refund> = [
    { key: 'id', title: 'Mã phiếu', width: 110, render: (_, r) => <span className="font-mono text-[13px] font-semibold">{r.id}</span> },
    { key: 'order', title: 'Mã đơn', width: 200, render: (_, r) => <Link to={`/pos/orders?id=${r.orderId}&from=2000-01-01`} className="font-mono text-[13px] text-[#2c6b7f] hover:underline">{r.orderId}</Link> },
    { key: 'customer', title: 'Khách', width: 160, render: (_, r) => customerOf.get(r.orderId) || '—' },
    { key: 'amount', title: 'Tổng hoàn', width: 120, align: 'right', render: (_, r) => <b className="text-violet-700">{money(r.amount)}</b> },
    { key: 'method', title: 'Phương thức', width: 120, render: (_, r) => METHOD_LABEL[r.method] },
    { key: 'reason', title: 'Lý do', width: 240, render: (_, r) => <span className="line-clamp-2">{r.reason}</span> },
    { key: 'by', title: 'Người tạo', width: 110, dataIndex: 'createdBy' },
    { key: 'at', title: 'Thời gian', width: 140, render: (_, r) => fmtDateTime(r.createdAt) },
    { key: 'status', title: 'Trạng thái', width: 150, render: (_, r) => <StatusPill {...REFUND_STATUS[r.status]} /> },
    { key: 'approver', title: 'Người duyệt', width: 150, render: (_, r) => r.approvedBy ? <span>{r.approvedBy}<div className="text-xs text-slate-400">{r.approvedAt && fmtDateTime(r.approvedAt)}</div></span> : '—' },
    {
      key: 'actions', title: '', width: 220, fixed: 'right',
      render: (_, r) => (
        <div className="flex justify-end gap-1.5">
          {r.status === 'pending' && canApprove(r) && <>
            <button onClick={() => modal.confirm({ title: `Duyệt phiếu ${r.id}?`, content: `Hoàn ${money(r.amount)}đ cho đơn ${r.orderId}.`, okText: 'Duyệt', cancelText: 'Hủy', onOk: () => { approveRefund(r.id, userName); message.success('Đã duyệt – chờ thu ngân chi tiền'); } })}
              className="flex h-8 items-center gap-1 rounded-lg bg-green-600 px-3 text-sm font-semibold text-white hover:bg-green-700"><Check className="h-4 w-4" /> Duyệt</button>
            <button onClick={() => setReject({ id: r.id, reason: '' })} className="flex h-8 items-center gap-1 rounded-lg border border-red-200 px-3 text-sm font-semibold text-red-600 hover:bg-red-50"><X className="h-4 w-4" /> Từ chối</button>
          </>}
          {r.status === 'pending' && !canApprove(r) && <span className="text-xs text-slate-400">{can('approve') ? 'Không tự duyệt phiếu của mình' : 'Chờ quản lý duyệt'}</span>}
          {r.status === 'approved' && (canPay(r)
            ? <button onClick={() => setPaying({ r, ref: r.txnRef })} className="flex h-8 items-center gap-1 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]"><HandCoins className="h-4 w-4" /> Chi tiền</button>
            : <span className="text-xs text-slate-400">Chi tại {branchName(r.branchId)}</span>)}
        </div>
      ),
    },
  ];

  const payNeedsRef = paying && METHODS_NEED_REF.includes(paying.r.method);
  const payValid = !payNeedsRef || /^[A-Za-z0-9]{4,50}$/.test(paying?.ref || '');

  return (
    <PosShell title="Hoàn tiền" subtitle="Phiếu từ 2.000.000đ hoặc đơn khác ngày cần quản lý duyệt; duyệt xong thu ngân chi tiền tại chi nhánh.">
      {ctx}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select allowClear placeholder="Chi nhánh" className="w-44" value={branch} onChange={setBranch} options={branchOptions} disabled={role === 'cashier'} />
        <Input.Search allowClear placeholder="Mã phiếu, mã đơn, khách…" value={q} onChange={e => setQ(e.target.value)} className="w-64" />
      </div>
      <Tabs activeKey={tab} onChange={k => setTab(k as TabKey)}
        items={TABS.map(t => ({ key: t.key, label: t.key === 'pending' ? <Badge count={count('pending')} offset={[12, -2]} size="small">{t.label}</Badge> : `${t.label} (${count(t.key)})` }))} />
      <Table<Refund>
        rowKey="id" size="middle" className="custom-ant-table" columns={columns} dataSource={data} scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }} locale={{ emptyText: 'Không có phiếu hoàn' }}
        rowClassName={r => (r.id === highlight ? '!bg-[#ecf6f9]' : '')}
        expandable={{
          expandedRowRender: r => (
            <div className="space-y-1 text-sm">
              {r.lines.map(l => <div key={l.lineKey} className="flex justify-between gap-4"><span>{l.name} × {l.qty}{l.stockAction && <span className="text-slate-400"> · {l.stockAction === 'restock' ? 'Nhập lại kho' : 'Hủy hàng'}</span>}</span><b>{money(l.amount)}</b></div>)}
              <div className="text-slate-500">Lý do: {r.reason}</div>
              {r.rejectReason && <div className="text-red-600">Lý do từ chối: {r.rejectReason}</div>}
              {r.txnRef && <div className="text-slate-500">Mã GD hoàn: <span className="font-mono">{r.txnRef}</span></div>}
            </div>
          ),
        }}
      />

      <Modal open={!!reject} title="Từ chối phiếu hoàn" onCancel={() => setReject(null)} okText="Từ chối" okButtonProps={{ danger: true, disabled: (reject?.reason.trim().length || 0) < 5 }} cancelText="Hủy" centered
        onOk={() => { if (reject) { rejectRefund(reject.id, userName, reject.reason.trim()); setReject(null); message.success('Đã từ chối'); } }}>
        <p className="mb-2 text-sm text-slate-600">Lý do từ chối (bắt buộc, tối thiểu 5 ký tự):</p>
        <Input.TextArea autoFocus rows={3} maxLength={300} value={reject?.reason} onChange={e => reject && setReject({ ...reject, reason: e.target.value })} />
      </Modal>

      <Modal open={!!paying} title={paying ? `Chi tiền hoàn ${paying.r.id}` : ''} onCancel={() => setPaying(null)} okText={`Đã chi ${paying ? money(paying.r.amount) : ''}đ`} cancelText="Hủy" centered
        okButtonProps={{ disabled: !payValid }}
        onOk={() => { if (paying) { payRefund(paying.r.id, userName, paying.ref); setPaying(null); message.success('Đã chi tiền hoàn – giao dịch âm đã ghi vào lịch sử'); } }}>
        {paying && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-violet-50 p-3">
              <div>Đơn <b className="font-mono">{paying.r.orderId}</b> · {customerOf.get(paying.r.orderId)}</div>
              <div>Phương thức: <b>{METHOD_LABEL[paying.r.method]}</b></div>
              <div className="text-2xl font-extrabold text-violet-700">{money(paying.r.amount)}đ</div>
            </div>
            {payNeedsRef && (
              <label className="block">
                <span className="mb-1 block font-medium">Mã giao dịch hoàn <span className="text-red-500">*</span></span>
                <Input size="large" value={paying.ref} maxLength={50} onChange={e => setPaying({ ...paying, ref: e.target.value.toUpperCase().replace(/\s/g, '') })} placeholder="VD: FT26100800123" />
              </label>
            )}
          </div>
        )}
      </Modal>
    </PosShell>
  );
}
